import React from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { HomePageStarredEntities } from '@backstage/plugin-home';
import { Page, Header, Content } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import qmulLogo from '../../assets/qm-logo-white.svg';
import { services, Service, Tool } from './Services';
import { TemplateScroller } from './TemplateScroller';
import { generateTemplateBackground } from './TemplateBackgrounds';
import {
  Card,
  Typography,
  Button,
  Tooltip,
  Link,
} from '@material-ui/core';
import { ComponentsTable } from './ComponentsTable';
import { useIdentityRoles } from '../../utils/useIdentityRoles';

const useStyles = makeStyles((theme) => ({
  logo: {
    maxHeight: '260px',
    width: 'auto',
    paddingRight: '2em',
    paddingBottom: '1em',
    backgroundColor: '#052556',
  },
  servicesSection: {
    width: '100%',
    overflow: 'hidden',
  },
  servicesHeader: {
    backgroundColor: '#052556',
    color: 'white',
    width: '100%',
    borderRadius: '8px',
  },
  servicesGrid: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'nowrap',
    '@media (max-width: 600px)': {
      gap: theme.spacing(2),
      overflowX: 'auto',
    },
  },
  serviceCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    aspectRatio: '1',
    width: '100%',
    maxWidth: '180px',
    margin: '0 auto',
  },
  servicesContent: {
    overflowX: 'hidden',
    padding: theme.spacing(2, 0),
    '@media (max-width: 600px)': {
      padding: theme.spacing(2),
    },
  },
  serviceIcon: {
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
  serviceLabel: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5, 1),
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
      marginLeft: theme.spacing(0.5),
    },
  },
  docsButton: {
    marginTop: 'auto',
    fontSize: '0.75rem',
    textTransform: 'none',
    padding: theme.spacing(0.5, 2),
    minWidth: '80px',
  },
  tooltip: {
    margin: '0 !important',
    position: 'relative',
  },
  contentContainer: {
    margin: '0 auto',
    padding: theme.spacing(0, 2),
  },
  componentTable: {
    '& .MuiTableContainer-root': {
      display: 'flex',
      justifyContent: 'center',
    },
    '& table': {
      width: 'auto',
      minWidth: '100%',
      maxWidth: '100%',
    },
    padding: theme.spacing(2, 2),
  },
  sectionHeader: {
    backgroundColor: '#052556',
    color: 'white',
    borderRadius: '8px',
  },
  launchButton: {
    marginTop: 'auto',
    fontSize: '0.75rem',
    textTransform: 'none',
    padding: theme.spacing(0.5, 2),
    minWidth: '80px',
  },
  documentationLink: {
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: '#1976d2',//  theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

export const HomePage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { isAdmin, isResearcher } = useIdentityRoles();
  const [templates, setTemplates] = React.useState<Entity[]>([]);

  React.useEffect(() => {
    const fetchTemplates = async () => {
      const response = await catalogApi.getEntities({
        filter: {
          kind: 'Template',
        },
      });
      setTemplates(response.items);
    };

    fetchTemplates();
  }, [catalogApi]);

  const templateItems = templates.map(template => ({
    title: template.metadata.name,
    description: template.metadata.description || '',
    imageUrl: generateTemplateBackground(template.metadata.name),
    onClick: () => {
      window.location.href = `/create/templates/default/${template.metadata.name}`;
    },
  }));

  const visibleServices = services.filter(s =>
    (!s.staffOnly || isAdmin || isResearcher) && (!s.adminOnly || isAdmin),
  );

  const tools: Tool[] = visibleServices.map((service: Service) => ({
    url: service.url,
    label: service.label,
    icon: (
      <img 
        src={service.icon} 
        alt={service.label} 
        // contain, not the default fill: the service logos are not all
        // square (jupyterhub.png is 490x297), and a fixed 45x45 box would
        // stretch a landscape logo out of shape.
        style={{ width: '45px', height: '45px', objectFit: 'contain' }} 
      />
    ),
    description: service.description,
    documentation: service.documentation,
  }));

  const ServiceCard = ({ service }: { service: Tool }) => {
    const classes = useStyles();

    return (
      <Tooltip 
        title={service.description || ''} 
        placement="top"
        arrow
        enterDelay={200}
        PopperProps={{
          modifiers: {
            offset: {
              enabled: true,
            },
          },
        }}
        classes={{
          tooltip: classes.tooltip,
        }}
      >
        <Card className={classes.serviceCard}>
          <div 
              className={classes.serviceIcon}
              onClick={() => window.open(service.url, '_blank')}
              style={{ cursor: 'pointer' }}
            >
            {service.icon}
          </div>
          <Typography className={classes.serviceLabel}>
            {service.label}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            className={classes.launchButton}
            onClick={() => window.open(service.url, '_blank')}
          >
            Launch
          </Button>
          {service.documentation && (
            <Link
              href={service.documentation}
              target="_blank"
              rel="noopener"
              className={classes.documentationLink}
            >
              Documentation
            </Link>
          )}
        </Card>
      </Tooltip>
    );
  };

  return (
    <Page themeId="home">
      <Header
        title="Unified Platform"
        subtitle="Queen Mary University of London"
        type="home"
      >
        <img
          src={qmulLogo}
          alt="Queen Mary University of London"
          className={classes.logo}
        />
      </Header>
      <Content>
        <div className={classes.contentContainer}>
          <Grid container direction="column" spacing={6}>
            <Grid item xs={12}>
              <div className={classes.servicesSection}>
                <div className={classes.servicesHeader}>
                  <Typography variant="h5">Services</Typography>
                </div>
                <div className={classes.servicesContent}>
                  <div className={classes.servicesGrid}>
                    {tools.map((tool, index) => (
                      <ServiceCard key={index} service={tool} />
                    ))}
                  </div>
                </div>
              </div>
            </Grid>

            <Grid item xs={12}>
              <TemplateScroller 
                title="Resource Templates" 
                templates={templateItems}
              />
            </Grid>
            <Grid item xs={12}>
              <div>
                <div className={classes.sectionHeader}>
                  <Typography variant="h5">My Components</Typography>
                </div>
                <div className={classes.componentTable}>
                  <ComponentsTable />
                </div>
              </div>
            </Grid>
            <Grid item xs={12}>
              <HomePageStarredEntities title="Favorites" />
            </Grid>
          </Grid>
        </div>
      </Content>
    </Page>
  );
};
